<template>
  <div class="product-card-drawer" data-testid="product-card-drawer">
    <div class="product-card-drawer__header">
      <AkHeading4>
        {{ t('account.brands.createCampaigns.editor.toolbar.insertProductCard.drawer.title') }}
      </AkHeading4>
      <AkParagraph>
        {{ t('account.brands.createCampaigns.editor.toolbar.insertProductCard.drawer.description', { max: MAX_COUNT }) }}
      </AkParagraph>
    </div>
    <ProductInlineSearch
      v-model:selected-products="selectedProducts"
      v-model:should-show-prices="shouldShowPrices"
      :image-cdn-service="imageCdnService"
      data-testid="product-inline-search"
    />
    <div class="product-card-drawer__footer">
      <div class="product-card-drawer__footer-inner">
        <AkButton size="lg" outlined data-testid="cancel-button" @click="emit('close')">
          {{ t('account.brands.createCampaigns.editor.toolbar.insertProductCard.drawer.cancel') }}
        </AkButton>
        <AkButton size="lg" data-testid="submit-button" :disabled="selectedProducts.length === 0" @click="emit('submit', selectedProducts, shouldShowPrices)">
          {{ t('account.brands.createCampaigns.editor.toolbar.insertProductCard.drawer.submit') }}
        </AkButton>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { AkHeading4, AkParagraph, AkButton } from '@ankorstore/design-system';
import { useI18n } from 'vue-i18n';
import { ProductInlineSearch } from '../shared/product-inline-search';
import { Amount } from '@client/monolith/core/types/amount';
import { ref } from 'vue';
import { watchEffect, toRefs } from 'vue';
import { ImageCdnService } from '../types';
import { MAX_COUNT } from './config';

type SimpleProduct = {
  uuid: string;
  name: string;
  imgSrc?: string;
  price: Amount;
  link: string;
};

const selectedProducts = ref<SimpleProduct[]>([]);
const shouldShowPrices = ref(true);

const props = defineProps<{
  preSelectedProducts: SimpleProduct[];
  imageCdnService: ImageCdnService;
}>();

const { preSelectedProducts } = toRefs(props);

defineOptions({
  name: 'ProductCardDrawer'
});

const emit = defineEmits<{
  (e: 'close'): void;
  (e: 'submit', products: SimpleProduct[], shouldShowPrices: boolean): void;
}>();

const { t } = useI18n();

watchEffect(() => {
  selectedProducts.value = preSelectedProducts.value;
});
</script>

<style lang="scss" scoped>
.product-card-drawer {
  @apply ds-p-6;

  &__header {
      @apply ds-mb-4 ds-border-b ds-border-neutral-300 ds-border-solid ds-pb-4;
  }

  &__footer {
    @apply ds-shadow-md ds-p-4 ds-absolute ds-bottom-0 ds-left-0 ds-w-full ds-flex;
  }

  // Flip twice to allow box shadow to appear on top of footer
  &__footer-inner, &__footer {
    transform: rotate(180deg);
    @apply ds-flex ds-gap-4;
  }
}
</style>
